import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { ConfigProvider, Flex, Pagination, Spin } from "antd";
import { Children, CSSProperties, ReactNode, useState } from "react";
import styled from "styled-components";

import { useTranslate } from "#src/hooks";

type PaginatedListProps = {
  children: ReactNode;
  itemsPerPage: number;
  loadingMore?: boolean;
  onPageChange?: (firstItemIndex: number) => void;
  style?: CSSProperties;
};

export const PaginatedList = ({
  children,
  itemsPerPage,
  loadingMore,
  onPageChange,
  style,
}: PaginatedListProps) => {
  const t = useTranslate();
  const items = Children.toArray(children);
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));
  const [currentPage, setCurrentPage] = useState(1);

  // Clamp if items shrink so the view never lands on an empty page.
  const safePage = Math.min(currentPage, totalPages);

  const goToPage = (page: number) => {
    const target = Math.max(1, Math.min(page, totalPages));
    if (target === safePage) return;
    setCurrentPage(target);
    onPageChange?.((target - 1) * itemsPerPage);
  };

  if (items.length === 0) return null;

  const slideWidth = `${100 / itemsPerPage}%`;
  const trackOffset = `${(safePage - 1) * 100}%`;

  return (
    <Flex vertical style={style}>
      <Flex align="center" style={{ width: "100%" }}>
        <ArrowButton
          aria-label={t("components.ContactWithPresenter.PaginatedList.previousPage")}
          onClick={() => goToPage(safePage - 1)}
          $hidden={safePage <= 1}
        >
          <LeftOutlined />
        </ArrowButton>
        <Viewport>
          <Track style={{ transform: `translateX(-${trackOffset})` }}>
            {items.map((child, i) => (
              <Slide key={i} style={{ flex: `0 0 ${slideWidth}` }}>
                {child}
              </Slide>
            ))}
          </Track>
        </Viewport>
        <ArrowButton
          aria-label={t("components.ContactWithPresenter.PaginatedList.nextPage")}
          onClick={() => goToPage(safePage + 1)}
          $hidden={safePage >= totalPages}
        >
          <RightOutlined />
        </ArrowButton>
      </Flex>
      {totalPages > 1 && (
        <Flex
          justify="center"
          align="center"
          gap={8}
          style={{ padding: "10px 0", position: "relative" }}
        >
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  itemActiveBg: "transparent",
                },
              },
            }}
          >
            <Pagination
              size="small"
              current={safePage}
              total={items.length}
              pageSize={itemsPerPage}
              onChange={goToPage}
              showSizeChanger={false}
              itemRender={(_page, type, originalElement) =>
                type === "prev" || type === "next" ? null : originalElement
              }
            />
          </ConfigProvider>
          {loadingMore && <Spin size="small" />}
        </Flex>
      )}
    </Flex>
  );
};

const Viewport = styled.div`
  flex: 1;
  overflow: hidden;
  width: 100%;
`;

const Track = styled.div`
  display: flex;
  flex-wrap: nowrap;
  transition: transform 0.3s ease;
  will-change: transform;
`;

const Slide = styled.div`
  min-width: 0;
`;

const ArrowButton = styled.button<{ $hidden?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${({ theme }) => theme.colorPrimary};
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
  pointer-events: ${({ $hidden }) => ($hidden ? "none" : "auto")};
`;
